/**
 * Application entry point.
 * Initialisation order (must not be changed):
 *   1. Sentry  — must capture errors from first render
 *   2. PostHog — COPPA-safe analytics (memory persistence, D-012)
 *   3. validateEnv — throws early with a clear message if vars missing
 *   4. BrowserRouter + provider tree rendered
 *   5. initVitals — after router mounts for correct LCP attribution
 *
 * Provider nesting order (must not be changed):
 *   BrowserRouter  — outermost: all routing hooks require this
 *   ErrorBoundary  — catches render errors before any provider throws
 *   AuthProvider   — auth state (ThemeProvider reads profile from it)
 *   ThemeProvider  — reads profile.grade_level → derives gradeTier
 *   ToastProvider  — independent, available to all screens
 *   AppRouter      — lazy-loads all screen-level components
 */
import { StrictMode }       from 'react'
import { createRoot }       from 'react-dom/client'
import { BrowserRouter }    from 'react-router-dom'
import { initSentry }       from '@/lib/sentry'
import { initPostHog }      from '@/lib/analytics'
import { initVitals }       from '@/lib/vitals'
import { validateEnv }      from '@/lib/validateEnv'
import { AuthProvider }     from '@/context/AuthContext'
import { ThemeProvider }    from '@/context/ThemeContext'
import { ToastProvider }    from '@/context/ToastContext'
import { ErrorBoundary }    from '@/components/ErrorBoundary'
import { AppRouter }        from '@/components/AppRouter'
import { UpdateBanner }     from '@/components/UpdateBanner'
import './styles/index.css'

// ── 1. Sentry — must stay upfront to capture first-render errors ─────────────
initSentry()

// ── 2. PostHog — deferred to idle time so it never blocks LCP ────────────────
// requestIdleCallback fires after the browser finishes painting the first frame.
// Falls back to a 3 s timeout on browsers without rIC (older Safari).
// Trade-off: the very first page_viewed event may be missed on sub-3 s bounces —
// acceptable; Sentry still catches all errors from frame 0.
if (typeof window !== 'undefined') {
  const deferPostHog = () => initPostHog()
  if ('requestIdleCallback' in window) {
    requestIdleCallback(deferPostHog, { timeout: 3000 })
  } else {
    setTimeout(deferPostHog, 3000)
  }
}

// ── 3. Env guard — throws with a clear message listing missing vars ───────────
validateEnv()

// ── 4. Render ─────────────────────────────────────────────────────────────────
const rootEl = document.getElementById('root')
if (rootEl == null) throw new Error('[Nudge] #root element not found in index.html')

createRoot(rootEl).render(
  <StrictMode>
    <BrowserRouter>
      <ErrorBoundary route="root">
        {/*
          Auth → Theme → Toast
          • ThemeProvider must be inside AuthProvider (reads profile.grade_level)
          • ToastProvider is independent but must be inside ErrorBoundary
        */}
        <AuthProvider>
          <ThemeProvider>
            <ToastProvider>
              <AppRouter />
              <UpdateBanner />
            </ToastProvider>
          </ThemeProvider>
        </AuthProvider>
      </ErrorBoundary>
    </BrowserRouter>
  </StrictMode>
)

// ── 5. Web Vitals — after router mount for correct LCP attribution ────────────
initVitals()

// ── 6. Service Worker — registered after render so SW never delays LCP ────────
// Uses VitePWA virtual module so registration errors are caught and don't
// become unhandled promise rejections in Sentry (previously logged as D-026).
import('virtual:pwa-register').then(({ registerSW }) => {
  const updateSW = registerSW({
    onNeedRefresh() {
      window.dispatchEvent(
        new CustomEvent('nudge:sw-update', { detail: { updateSW } })
      )
    },
    onRegisteredSW(_swUrl: string, _reg: ServiceWorkerRegistration | undefined) {
      // SW registered; update prompt handles reload
    },
    onRegisterError(error: unknown) {
      // SW unavailable = no offline support, app still works fully online
      console.warn('[Nudge] Service worker registration failed (non-critical):', error)
    },
  })
}).catch(() => {
  // virtual:pwa-register is stripped when PWA plugin is not active — ignore
})
